// Announces server-rendered Bootstrap success/status alerts to screen readers.
// A live region only fires for content inserted after it is registered, so the
// empty #ss-live-status region (present in markup at page load) is populated
// after load with the alert text — e.g. the directory search
// "Success! 1 Search Result" message (SS-4698).
//
// The text is cleared a few seconds after announcing so a screen-reader user
// navigating the page linearly does not encounter the message twice: once from
// this hidden region near the top and once from the visible alert further down.

const LIVE_REGION_ID = 'ss-live-status';
const SUCCESS_ALERT_SELECTOR = '.alert.alert-success';
// Clear then set after a short delay so the region is seen to change, which
// NVDA/VoiceOver require in order to announce.
const ANNOUNCE_DELAY_MS = 200;
// Polite live-region updates are queued behind whatever the screen reader is
// already speaking (e.g. reading the page on load), so the message must stay in
// the DOM well past the point it is set to survive until it is actually spoken.
// Clearing too early cuts the announcement off. This is generous enough to
// out-last that queue while still removing the duplicate copy from the reading
// order (the reviewer's concern) shortly after.
const CLEAR_DELAY_MS = 10000;

const getAlertMessage = (alert: Element): string => {
    // Read the message without the close button's "×" glyph, which would
    // otherwise be announced as "times" before "Success!".
    const clone = alert.cloneNode(true) as HTMLElement;
    clone.querySelector('.close')?.remove();
    return (clone.textContent ?? '').replace(/\s+/g, ' ').trim();
};

const announceStatusAlert = (): void => {
    const liveRegion = document.getElementById(LIVE_REGION_ID);
    if (!liveRegion) return;

    const alert = document.querySelector(SUCCESS_ALERT_SELECTOR);
    if (!alert) return;

    const message = getAlertMessage(alert);
    if (!message) return;

    liveRegion.textContent = '';
    window.setTimeout(() => {
        liveRegion.textContent = message;
        window.setTimeout(() => {
            liveRegion.textContent = '';
        }, CLEAR_DELAY_MS);
    }, ANNOUNCE_DELAY_MS);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', announceStatusAlert);
} else {
    announceStatusAlert();
}
